import { createFileRoute } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Button, Card } from '@/components';
import { apiJson, ApiError } from '@/lib/api';
import { DivorceCrawlerPanel } from '@/components/admin/DivorceCrawlerPanel';

type VersionSummary = {
  id: number;
  jurisdiction_code: string;
  form_number: string | null;
  title: string;
  revision_label: string | null;
  source_url: string;
  status: string;
  fetched_at: string;
  purpose: string | null;
  flags: string[];
  field_count?: number;
};

type FieldMap = {
  id: number;
  status: string;
  proposed_by: string;
  answer_schema_version: number;
  mapping: Array<Record<string, unknown>>;
  notes: string | null;
};

type VersionDetail = VersionSummary & {
  sha256: string;
  spaces_key: string;
  page_count: number | null;
  classification: Record<string, unknown> | null;
  pdf_url: string | null;
  field_maps: FieldMap[];
  reviewed_by: string | null;
  reviewed_at: string | null;
};

function parseDetail(err: unknown): string {
  if (err instanceof ApiError && err.bodyText) {
    try {
      const j = JSON.parse(err.bodyText) as { detail?: unknown };
      if (typeof j.detail === 'string') return j.detail;
      if (j.detail) return JSON.stringify(j.detail);
    } catch {
      /* ignore */
    }
    return err.bodyText;
  }
  return err instanceof Error ? err.message : 'Request failed';
}

const STATUS_BADGE: Record<string, string> = {
  pending: 'bg-amber-50 text-amber-800',
  approved: 'bg-green-50 text-green-800',
  rejected: 'bg-red-50 text-red-700',
  superseded: 'bg-nova-muted text-nova-muted-foreground',
  proposed: 'bg-blue-50 text-blue-800',
};

export const Route = createFileRoute('/admin/divorce-forms')({
  component: DivorceFormsPage,
});

function DivorceFormsPage() {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState('pending');
  const [jurisdictionFilter, setJurisdictionFilter] = useState('');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [actionError, setActionError] = useState<string | null>(null);

  const listQuery = useQuery({
    queryKey: ['admin', 'divorce-forms', statusFilter, jurisdictionFilter],
    queryFn: () =>
      apiJson<VersionSummary[]>(
        getToken,
        `/v1/admin/divorce/forms?status=${encodeURIComponent(statusFilter)}${
          jurisdictionFilter
            ? `&jurisdiction=${encodeURIComponent(jurisdictionFilter)}`
            : ''
        }`,
      ),
  });

  const detailQuery = useQuery({
    queryKey: ['admin', 'divorce-forms', 'detail', selectedId],
    enabled: selectedId != null,
    queryFn: () =>
      apiJson<VersionDetail>(getToken, `/v1/admin/divorce/forms/${selectedId}`),
  });

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['admin', 'divorce-forms'] });
    void qc.invalidateQueries({ queryKey: ['admin', 'divorce-sources'] });
  };

  const versionAction = useMutation({
    mutationFn: ({ id, action }: { id: number; action: 'approve' | 'reject' }) =>
      apiJson(getToken, `/v1/admin/divorce/forms/${id}/${action}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reviewed_by: 'admin' }),
      }),
    onSuccess: () => {
      setActionError(null);
      invalidate();
    },
    onError: (err) => setActionError(parseDetail(err)),
  });

  const mappingAction = useMutation({
    mutationFn: ({ id, action }: { id: number; action: 'approve' | 'reject' }) =>
      apiJson(getToken, `/v1/admin/divorce/mappings/${id}/${action}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reviewed_by: 'admin' }),
      }),
    onSuccess: () => {
      setActionError(null);
      invalidate();
    },
    onError: (err) => setActionError(parseDetail(err)),
  });

  const bulkReject = useMutation({
    mutationFn: (versionIds: number[]) =>
      apiJson(getToken, '/v1/admin/divorce/forms/bulk-reject', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          version_ids: versionIds,
          reviewed_by: 'admin',
          notes: 'Bulk reject from review queue',
        }),
      }),
    onSuccess: () => {
      setActionError(null);
      setSelectedIds([]);
      invalidate();
    },
    onError: (err) => setActionError(parseDetail(err)),
  });

  const rejectSeedJunk = useMutation({
    mutationFn: () =>
      apiJson(getToken, '/v1/admin/divorce/forms/bulk-reject-seed', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          reviewed_by: 'admin',
          notes: 'Seed reject patterns',
        }),
      }),
    onSuccess: () => {
      setActionError(null);
      setSelectedIds([]);
      invalidate();
    },
    onError: (err) => setActionError(parseDetail(err)),
  });

  const catalogPending = useMutation({
    mutationFn: () =>
      apiJson(getToken, '/v1/admin/divorce/forms/catalog-pending', {
        method: 'POST',
      }),
    onSuccess: () => {
      setActionError(null);
      invalidate();
    },
    onError: (err) => setActionError(parseDetail(err)),
  });

  const catalogOne = useMutation({
    mutationFn: (id: number) =>
      apiJson(getToken, `/v1/admin/divorce/forms/${id}/catalog`, {
        method: 'POST',
      }),
    onSuccess: () => {
      setActionError(null);
      invalidate();
    },
    onError: (err) => setActionError(parseDetail(err)),
  });

  const versions = listQuery.data ?? [];
  const detail = detailQuery.data;

  return (
    <div className='mx-auto max-w-6xl space-y-4'>
      <div>
        <h1 className='text-2xl font-semibold text-nova-foreground'>Divorce form review</h1>
        <p className='mt-1 text-sm text-nova-muted-foreground'>
          The human gate: nothing is served or filled until approved here. Mappings can
          only be approved once their form version is approved. Reject seed junk applies
          the regexes in{' '}
          <code>data/divorce_registry/reject_patterns.json</code> (TX non-divorce, IL
          instruction/HR sheets, CA FL-300/320/330/335 RFOs) and never touches approved
          rows. Catalog pending classifies unclassified pending versions — it never
          auto-approves.
        </p>
      </div>

      <Card className='p-4'>
        <DivorceCrawlerPanel onError={setActionError} />
      </Card>

      <Card className='p-4'>
        <div className='flex flex-wrap items-center gap-3'>
          <label className='text-sm text-nova-muted-foreground'>
            Status{' '}
            <select
              className='ml-1 rounded border border-nova-input px-2 py-1 text-sm'
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              {['pending', 'approved', 'rejected', 'superseded', 'all'].map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </label>
          <label className='text-sm text-nova-muted-foreground'>
            State{' '}
            <input
              className='ml-1 w-16 rounded border border-nova-input px-2 py-1 text-sm uppercase'
              placeholder='CA'
              maxLength={2}
              value={jurisdictionFilter}
              onChange={(e) => setJurisdictionFilter(e.target.value.toUpperCase())}
            />
          </label>
          {listQuery.isFetching ? (
            <span className='text-xs text-nova-muted-foreground'>Loading…</span>
          ) : (
            <span className='text-xs text-nova-muted-foreground'>{versions.length} versions</span>
          )}
          <Button
            variant='secondary'
            onClick={() => void catalogPending.mutate()}
            disabled={catalogPending.isPending}
          >
            Catalog pending
          </Button>
          <Button
            variant='secondary'
            onClick={() => void rejectSeedJunk.mutate()}
            disabled={rejectSeedJunk.isPending}
          >
            Reject seed junk
          </Button>
          <Button
            variant='secondary'
            onClick={() => void bulkReject.mutate(selectedIds)}
            disabled={selectedIds.length === 0 || bulkReject.isPending}
          >
            Reject selected ({selectedIds.length})
          </Button>
        </div>
      </Card>

      {actionError ? <p className='text-sm text-red-600'>{actionError}</p> : null}

      <div className='grid gap-4 lg:grid-cols-2'>
        <Card className='overflow-hidden p-0'>
          <table className='w-full text-sm'>
            <thead>
              <tr className='border-b border-nova-border text-left text-xs uppercase text-nova-muted-foreground'>
                <th className='px-3 py-2'>
                  <input
                    type='checkbox'
                    checked={
                      versions.length > 0 && selectedIds.length === versions.length
                    }
                    onChange={(e) =>
                      setSelectedIds(e.target.checked ? versions.map((v) => v.id) : [])
                    }
                  />
                </th>
                <th className='px-3 py-2'>State</th>
                <th className='px-3 py-2'>Form</th>
                <th className='px-3 py-2'>Purpose</th>
                <th className='px-3 py-2'>Fields</th>
                <th className='px-3 py-2'>Status</th>
                <th className='px-3 py-2'>Flags</th>
              </tr>
            </thead>
            <tbody>
              {versions.map((v) => (
                <tr
                  key={v.id}
                  onClick={() => setSelectedId(v.id)}
                  className={`cursor-pointer border-b border-nova-border hover:bg-nova-accent ${
                    selectedId === v.id ? 'bg-nova-primary/5' : ''
                  }`}
                >
                  <td className='px-3 py-2' onClick={(e) => e.stopPropagation()}>
                    <input
                      type='checkbox'
                      checked={selectedIds.includes(v.id)}
                      onChange={(e) =>
                        setSelectedIds((ids) =>
                          e.target.checked
                            ? [...ids, v.id]
                            : ids.filter((id) => id !== v.id),
                        )
                      }
                    />
                  </td>
                  <td className='px-3 py-2 font-medium'>{v.jurisdiction_code}</td>
                  <td className='px-3 py-2'>
                    {v.form_number ?? '—'}{' '}
                    <span className='text-nova-muted-foreground'>{v.title.slice(0, 40)}</span>
                  </td>
                  <td className='px-3 py-2'>{v.purpose ?? '—'}</td>
                  <td className='px-3 py-2 text-xs text-nova-muted-foreground'>
                    {v.field_count ?? 0}
                  </td>
                  <td className='px-3 py-2'>
                    <span
                      className={`rounded px-1.5 py-0.5 text-xs ${STATUS_BADGE[v.status] ?? ''}`}
                    >
                      {v.status}
                    </span>
                  </td>
                  <td className='px-3 py-2 text-xs text-amber-700'>
                    {v.flags.join(', ')}
                  </td>
                </tr>
              ))}
              {versions.length === 0 && !listQuery.isLoading ? (
                <tr>
                  <td className='px-3 py-4 text-center text-nova-muted-foreground' colSpan={7}>
                    No versions for this filter.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </Card>

        <Card className='p-4'>
          {!detail ? (
            <p className='py-12 text-center text-sm text-nova-muted-foreground'>
              Select a version to review.
            </p>
          ) : (
            <div className='space-y-4'>
              <div>
                <h2 className='text-lg font-medium text-nova-foreground'>
                  {detail.jurisdiction_code} {detail.form_number ?? ''}{' '}
                  {detail.revision_label ? `(${detail.revision_label})` : ''}
                </h2>
                <p className='text-sm text-nova-muted-foreground'>{detail.title || 'Untitled'}</p>
                <p className='mt-1 break-all text-xs text-nova-muted-foreground'>
                  {detail.source_url}
                </p>
                <p className='mt-1 text-xs text-nova-muted-foreground'>sha256 {detail.sha256.slice(0, 16)}…</p>
                {detail.pdf_url ? (
                  <a
                    href={detail.pdf_url}
                    target='_blank'
                    rel='noopener noreferrer'
                    className='mt-1 inline-block text-sm font-medium text-nova-primary hover:underline'
                  >
                    View PDF
                  </a>
                ) : null}
              </div>

              <div className='flex gap-2'>
                <Button
                  onClick={() =>
                    void versionAction.mutate({ id: detail.id, action: 'approve' })
                  }
                  disabled={detail.status === 'approved' || versionAction.isPending}
                >
                  Approve version
                </Button>
                <Button
                  variant='secondary'
                  onClick={() =>
                    void versionAction.mutate({ id: detail.id, action: 'reject' })
                  }
                  disabled={detail.status === 'rejected' || versionAction.isPending}
                >
                  Reject
                </Button>
                <Button
                  variant='secondary'
                  onClick={() => void catalogOne.mutate(detail.id)}
                  disabled={catalogOne.isPending}
                >
                  Catalog
                </Button>
              </div>

              <div>
                <h3 className='mb-2 text-sm font-semibold text-nova-foreground'>
                  Field mappings
                </h3>
                {detail.field_maps.length === 0 ? (
                  <p className='text-sm text-nova-muted-foreground'>No mappings proposed yet.</p>
                ) : (
                  detail.field_maps.map((m) => (
                    <div key={m.id} className='mb-3 rounded border border-nova-border p-3'>
                      <div className='mb-1 flex items-center gap-2 text-xs'>
                        <span
                          className={`rounded px-1.5 py-0.5 ${STATUS_BADGE[m.status] ?? ''}`}
                        >
                          {m.status}
                        </span>
                        <span className='text-nova-muted-foreground'>
                          by {m.proposed_by} · schema v{m.answer_schema_version} ·{' '}
                          {m.mapping.length} entries
                        </span>
                      </div>
                      <pre className='max-h-48 overflow-auto rounded bg-nova-muted p-2 text-xs'>
                        {JSON.stringify(m.mapping, null, 1)}
                      </pre>
                      <div className='mt-2 flex gap-2'>
                        <Button
                          onClick={() =>
                            void mappingAction.mutate({ id: m.id, action: 'approve' })
                          }
                          disabled={
                            m.status === 'approved' ||
                            detail.status !== 'approved' ||
                            mappingAction.isPending
                          }
                        >
                          Approve mapping
                        </Button>
                        <Button
                          variant='secondary'
                          onClick={() =>
                            void mappingAction.mutate({ id: m.id, action: 'reject' })
                          }
                          disabled={m.status === 'rejected' || mappingAction.isPending}
                        >
                          Reject
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {detail.classification ? (
                <details>
                  <summary className='cursor-pointer text-sm font-semibold text-nova-foreground'>
                    Classification + inventory
                  </summary>
                  <pre className='mt-2 max-h-64 overflow-auto rounded bg-nova-muted p-2 text-xs'>
                    {JSON.stringify(detail.classification, null, 1)}
                  </pre>
                </details>
              ) : null}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
